import { Form, LinkedInLogin } from '@/components';
import { MasterTranslation, SignUpSource } from '@/constants';
import { useGlobalState, useResponsive } from '@/hooks';
import { LoginMethod, ResponsiveCollection } from '@/interfaces';
import { Divider } from '@/modules/pmt/components/Divider';
import { useSignIn } from '@/modules/pmt/components/TrackingOnboardingPopup/hooks/useSignIn.hook';
import { useEmailSyncContext } from '@/modules/pmt/context';
import type { ITrackingRightPanel } from '@/modules/pmt/interfaces';
import Image from 'next/image';
import { LoginEsyncButton } from '../../../../../LoginEsyncButton';
import { LoginPMTButton } from '../../../../../LoginPMTButton';
import { ButtonList, JoinUsMainText, JoinUsSubtext, PopUpTexts, PopupGraphic, SignUpWrapper } from './SignUp.styles';

const SignUp: React.FC<ITrackingRightPanel> = ({
  heroImage,
  joinUsDesktopMainText,
  joinUsSubtext,
  desktopGoogleCta,
  identifier,
  subPmcNewsletterText,
  zapierHookId,
  loginByEmail = true,
  delayTime, // for GTM
}) => {
  const { t } = useGlobalState();
  const { isDesktopPMT } = useResponsive([ResponsiveCollection.DesktopPMT]);

  const { handleSuccess, getLoginButtonProps, triggerGtmClickJoinUs, EmailField, CheckBoxField, cta } = useSignIn({
    identifier,
    subPmcNewsletterText,
    delayTime,
    zapierHookId,
  });

  const { isEmailSyncAllowed } = useEmailSyncContext();
  const isESPopup = isEmailSyncAllowed;

  const fields = isESPopup ? [EmailField] : [EmailField, CheckBoxField];

  return (
    <SignUpWrapper>
      {/* Graphic */}
      {isESPopup && heroImage?.url && (
        <PopupGraphic isDesktop={isDesktopPMT}>
          <Image alt='ES Onboarding Graphic' src={heroImage.url} width={296} height={isDesktopPMT ? 140 : 160} />
        </PopupGraphic>
      )}
      {/* Texts */}
      <PopUpTexts isESPopup={isESPopup}>
        <JoinUsMainText isDesktop={isDesktopPMT}>{joinUsDesktopMainText}</JoinUsMainText>
        <JoinUsSubtext>{joinUsSubtext}</JoinUsSubtext>
      </PopUpTexts>
      {/* Action Buttons */}
      <ButtonList isESPopup={isESPopup}>
        {/* Google */}
        {!isESPopup ? (
          <LoginPMTButton {...getLoginButtonProps(LoginMethod.Google)} />
        ) : (
          <LoginEsyncButton
            googleCta={desktopGoogleCta}
            handleClick={() => triggerGtmClickJoinUs(LoginMethod.Google)}
            handleSuccess={handleSuccess}
          />
        )}
        {/* LinkedIn */}
        {!isESPopup && (
          <LinkedInLogin
            {...getLoginButtonProps(LoginMethod.LinkedIn)}
            buttonText={t(MasterTranslation.ContinueWithLinkedIn)}
          />
        )}
      </ButtonList>
      {!!loginByEmail && (
        <>
          <Divider>{t(isESPopup ? MasterTranslation.TrackManually : MasterTranslation.Or)}</Divider>
          {/* Email */}
          <Form
            formFieldList={fields}
            buttonText={t(MasterTranslation.Continue)}
            submittingButtonText={t(MasterTranslation.Submitting)}
            promptBoxContent={t(MasterTranslation.ExistEmailPromptBox)}
            signUpSource={SignUpSource.ConsumerWeb}
            isEmailForm
            isPMTButton
            truncate
            onSuccess={() => triggerGtmClickJoinUs(LoginMethod.Email)}
            cta={cta}
          />
        </>
      )}
    </SignUpWrapper>
  );
};

export default SignUp;
